import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Lock, Ban, Flag, Bot, AlertTriangle, Unlock } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonCard, Spinner, Pill, NeonButton } from "../../components/Neon";
import { gbp } from "../../lib/format";

export default function AdminSecurity() {
  const [loading, setLoading] = useState(true);
  const [sec, setSec] = useState(null);
  const [losses, setLosses] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const load = () =>
    Promise.allSettled([api.get("/admin/security"), api.get("/admin/loss-warnings"), api.get("/admin/high-value-alerts")])
      .then(([s, l, a]) => {
        if (s.status === "fulfilled") setSec(s.value.data);
        if (l.status === "fulfilled") setLosses(l.value.data);
        if (a.status === "fulfilled") setAlerts(Array.isArray(a.value.data) ? a.value.data : []);
      })
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(getErr(e)); } };
  const unlock = (u) => act(() => api.post(`/admin/security/unlock/${u.id || u.user_id}`), "Account unlocked.");
  const aiReview = (u) => act(() => api.post(`/admin/security/ai-review/${u.id || u.user_id}`), "AI review requested.");
  const unban = (b) => act(() => api.post("/admin/security/unban", { ip: b.ip || b.address || b }), "IP unbanned.");
  const ackAlert = (al) => act(() => api.post(`/admin/high-value-alerts/${al.id}/ack`), "Alert acknowledged.");

  if (loading) return <Spinner />;
  const p = sec?.policy || {};
  const locked = sec?.locked_accounts || [];
  const bans = sec?.ip_bans || [];
  const flagged = sec?.flagged_accounts || [];
  const atRisk = losses?.at_risk || [];

  const Metric = ({ label, value, color = "cyan" }) => (
    <NeonCard variant="flow" innerClassName="p-3 text-center"><p className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{label}</p><p className={`mt-1 font-display text-xl text-[var(--neon-${color})]`}>{value}</p></NeonCard>
  );

  return (
    <div data-testid="admin-security" className="max-w-4xl">
      <div className="flex items-center gap-2"><ShieldAlert className="h-6 w-6 text-[var(--neon-pink)]" /><h2 className="font-display uppercase text-2xl text-white">Security &amp; Risk</h2></div>

      {/* Key metrics */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric label="Locked accounts" value={locked.length} color="gold" />
        <Metric label="IP bans" value={bans.length} color="pink" />
        <Metric label="Flagged" value={flagged.length} color="pink" />
        <Metric label="Under-18 attempts" value={sec?.minor_attempts ?? 0} color="gold" />
      </div>

      {/* Policy */}
      <NeonCard variant="cyan" className="mt-4" innerClassName="p-5">
        <p className="font-display uppercase text-sm tracking-widest glow-cyan">Security policy</p>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><p className="text-[11px] text-[var(--text-muted)]">Token TTL</p><p className="text-white">{p.token_ttl_days}d</p></div>
          <div><p className="text-[11px] text-[var(--text-muted)]">Min password</p><p className="text-white">{p.password_min_len}</p></div>
          <div><p className="text-[11px] text-[var(--text-muted)]">Lockout after</p><p className="text-white">{p.lockout_after} fails</p></div>
          <div><p className="text-[11px] text-[var(--text-muted)]">Lockout time</p><p className="text-white">{p.lockout_minutes}m</p></div>
          <div><p className="text-[11px] text-[var(--text-muted)]">Rate limit</p><p className="text-white">{p.rate_limit_max}/{p.rate_limit_window}s</p></div>
          <div><p className="text-[11px] text-[var(--text-muted)]">Ban time</p><p className="text-white">{p.ban_minutes}m</p></div>
          <div><p className="text-[11px] text-[var(--text-muted)]">Ban threshold</p><p className="text-white">{p.ban_threshold}</p></div>
          <div><p className="text-[11px] text-[var(--text-muted)]">Failed logins</p><p className="text-white">{sec?.failed_login_accounts ?? 0}</p></div>
        </div>
      </NeonCard>

      {/* Locked accounts */}
      <NeonCard variant="flow" className="mt-4" innerClassName="p-5">
        <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-[var(--neon-gold)]" /><p className="font-display uppercase text-sm tracking-widest text-white">Locked accounts</p></div>
        {locked.length === 0 ? <p className="mt-2 text-sm text-[var(--text-muted)]">None \u2014 all clear.</p> : (
          <div className="mt-3 divide-y divide-white/10">
            {locked.map((u, i) => (
              <div key={u.id || i} className="flex items-center justify-between py-2"><span className="text-sm text-white">{u.email || u.id}</span>
                <NeonButton variant="outlineCyan" onClick={() => unlock(u)} data-testid={`unlock-${i}`}><Unlock className="h-3.5 w-3.5" /> Unlock</NeonButton></div>
            ))}
          </div>
        )}
      </NeonCard>

      {/* IP bans */}
      <NeonCard variant="flow" className="mt-4" innerClassName="p-5">
        <div className="flex items-center gap-2"><Ban className="h-4 w-4 text-[var(--neon-pink)]" /><p className="font-display uppercase text-sm tracking-widest text-white">IP bans</p></div>
        {bans.length === 0 ? <p className="mt-2 text-sm text-[var(--text-muted)]">No active bans.</p> : (
          <div className="mt-3 divide-y divide-white/10">
            {bans.map((b, i) => (
              <div key={i} className="flex items-center justify-between py-2"><span className="text-sm text-white font-mono">{b.ip || b.address || String(b)}</span>
                <NeonButton variant="outlineCyan" onClick={() => unban(b)} data-testid={`unban-${i}`}>Unban</NeonButton></div>
            ))}
          </div>
        )}
      </NeonCard>

      {/* Flagged accounts */}
      <NeonCard variant="flow" className="mt-4" innerClassName="p-5">
        <div className="flex items-center gap-2"><Flag className="h-4 w-4 text-[var(--neon-pink)]" /><p className="font-display uppercase text-sm tracking-widest text-white">Flagged accounts</p></div>
        {flagged.length === 0 ? <p className="mt-2 text-sm text-[var(--text-muted)]">No flagged accounts.</p> : (
          <div className="mt-3 divide-y divide-white/10">
            {flagged.map((u, i) => (
              <div key={u.id || i} className="flex items-center justify-between py-2"><div><p className="text-sm text-white">{u.email || u.id}</p>{u.reason && <p className="text-[11px] text-[var(--text-muted)]">{u.reason}</p>}</div>
                <NeonButton variant="outlinePink" onClick={() => aiReview(u)} data-testid={`ai-review-${i}`}><Bot className="h-3.5 w-3.5" /> AI Review</NeonButton></div>
            ))}
          </div>
        )}
      </NeonCard>

      {/* High-value alerts */}
      <NeonCard variant="soft" className="mt-4" innerClassName="p-5">
        <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[var(--neon-gold)]" /><p className="font-display uppercase text-sm tracking-widest text-white">High-value alerts</p></div>
        {alerts.length === 0 ? <p className="mt-2 text-sm text-[var(--text-muted)]">No open high-value alerts.</p> : (
          <div className="mt-3 divide-y divide-white/10">
            {alerts.map((al, i) => (
              <div key={al.id || i} className="flex items-center justify-between py-2"><span className="text-sm text-white">{al.message || al.title || al.email || `Alert ${i + 1}`}</span>
                <NeonButton variant="outlineCyan" onClick={() => ackAlert(al)} data-testid={`ack-${i}`}>Acknowledge</NeonButton></div>
            ))}
          </div>
        )}
      </NeonCard>

      {/* Loss warnings */}
      <NeonCard variant="flow" className="mt-4" innerClassName="p-5">
        <div className="flex items-center justify-between"><div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[var(--neon-pink)]" /><p className="font-display uppercase text-sm tracking-widest text-white">Loss warnings</p></div>{losses && <Pill color={atRisk.length ? "pink" : "green"}>{losses.at_risk_count ?? atRisk.length} at risk</Pill>}</div>
        {atRisk.length === 0 ? <p className="mt-2 text-sm text-[var(--text-muted)]">No competitions projected to make a loss.</p> : (
          <div className="mt-3 space-y-2">
            {atRisk.map((c) => (
              <div key={c.id} className="rounded-lg border border-[rgba(255,45,149,.4)] p-3 text-sm">
                <p className="text-white">{c.title}</p>
                <p className="text-[11px] text-[var(--text-muted)]">{c.tickets_sold}/{c.total_tickets} sold \u00b7 {c.pct_elapsed}% elapsed \u00b7 projected net <span className="text-[var(--neon-pink)]">{gbp(c.projected_net)}</span></p>
              </div>
            ))}
          </div>
        )}
      </NeonCard>
    </div>
  );
}
